/** MI DOMINO — accurate flag artwork for the most popular flag tables.
 *  Each flag paints the felt as the real flag (simplified emblems). Keyed by
 *  catalog id. Flags not listed here keep the old stripe gradient. */

const svg = (w: number, h: number, body: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none">${body}</svg>`;

const usStars = (() => {
  let s = '';
  for (let i = 0; i < 9; i++) {
    const y = 50 + i * 55;
    const xs = i % 2 === 0 ? [60, 180, 300, 420, 540, 660] : [120, 240, 360, 480, 600];
    for (const x of xs) s += `<circle cx="${x}" cy="${y}" r="20" fill="#fff"/>`;
  }
  return s;
})();

const usStripes = (() => {
  let s = '';
  for (let i = 0; i < 13; i++) s += `<rect x="0" y="${(i * 1000) / 13}" width="1900" height="${1000 / 13 + 1}" fill="${i % 2 === 0 ? '#b22234' : '#fff'}"/>`;
  return s;
})();

export const FLAG_ART: Record<string, string> = {
  /* República Dominicana — white cross, blue & red quarters (coat of arms simplified) */
  'flag-do': svg(900, 600,
    `<rect width="900" height="600" fill="#ce1126"/>
     <rect x="0" y="0" width="450" height="300" fill="#002d62"/>
     <rect x="450" y="300" width="450" height="300" fill="#002d62"/>
     <rect x="360" y="0" width="180" height="600" fill="#fff"/>
     <rect x="0" y="210" width="900" height="180" fill="#fff"/>`),

  /* Puerto Rico — red/white stripes, blue triangle, white star */
  'flag-pr': svg(900, 600,
    `<rect width="900" height="600" fill="#fff"/>
     <rect x="0" y="0" width="900" height="120" fill="#ed0000"/>
     <rect x="0" y="240" width="900" height="120" fill="#ed0000"/>
     <rect x="0" y="480" width="900" height="120" fill="#ed0000"/>
     <polygon points="0,0 0,600 520,300" fill="#0050f0"/>
     <polygon points="200.0,210.0 220.2,272.2 285.6,272.2 232.7,310.6 252.9,372.8 200.0,334.4 147.1,372.8 167.3,310.6 114.4,272.2 179.8,272.2" fill="#fff"/>`),

  /* Cuba — blue/white stripes, red triangle, white star */
  'flag-cu': svg(600, 300,
    `<rect width="600" height="300" fill="#fff"/>
     <rect x="0" y="0" width="600" height="60" fill="#002a8f"/>
     <rect x="0" y="120" width="600" height="60" fill="#002a8f"/>
     <rect x="0" y="240" width="600" height="60" fill="#002a8f"/>
     <polygon points="0,0 0,300 260,150" fill="#cf142b"/>
     <polygon points="100.0,108.0 109.4,137.0 139.9,137.0 115.3,155.0 124.7,184.0 100.0,166.0 75.3,184.0 84.7,155.0 60.1,137.0 90.6,137.0" fill="#fff"/>`),

  /* Venezuela — yellow / blue / red, arc of stars */
  'flag-ve': svg(900, 600,
    `<rect width="900" height="300" fill="#fcd116"/>
     <rect y="300" width="900" height="150" fill="#00247d"/>
     <rect y="450" width="900" height="150" fill="#cf142b"/>
     ${[0,1,2,3,4,5,6,7].map(i => { const a = (-70 + i * 20) * Math.PI / 180; return `<circle cx="${450 + 260 * Math.cos(a)}" cy="${375 + 90 * Math.sin(a) * 0.9}" r="12" fill="#fff"/>`; }).join('')}`),

  /* Colombia — yellow half, blue and red quarters */
  'flag-co': svg(900, 600,
    `<rect width="900" height="300" fill="#fcd116"/>
     <rect y="300" width="900" height="150" fill="#003893"/>
     <rect y="450" width="900" height="150" fill="#ce1126"/>`),

  /* México — green / white / red vertical thirds (eagle simplified) */
  'flag-mx': svg(900, 600,
    `<rect width="300" height="600" fill="#006847"/>
     <rect x="300" width="300" height="600" fill="#fff"/>
     <rect x="600" width="300" height="600" fill="#ce1126"/>
     <circle cx="450" cy="300" r="48" fill="#8c6a1b"/>`),

  /* Estados Unidos — 13 stripes, blue canton, 50 stars */
  'flag-us': svg(1900, 1000,
    `${usStripes}
     <rect x="0" y="0" width="760" height="538" fill="#3c3b6e"/>
     ${usStars}`),

  /* Brasil — green field, yellow rhombus, blue globe with white band */
  'flag-br': svg(720, 504,
    `<rect width="720" height="504" fill="#009c3b"/>
     <polygon points="360,40 680,252 360,464 40,252" fill="#ffdf00"/>
     <circle cx="360" cy="252" r="110" fill="#002776"/>
     <path d="M262 226 Q360 200 458 236" stroke="#fff" stroke-width="22" fill="none"/>`),

  /* Jamaica — green/gold/black saltire */
  'flag-jm': svg(900, 500,
    `<rect width="900" height="500" fill="#009b3a"/>
     <polygon points="0,0 120,0 900,430 900,500 780,500 0,70" fill="#fed100"/>
     <polygon points="900,0 780,0 0,430 0,500 120,500 900,70" fill="#fed100"/>
     <polygon points="0,0 60,0 900,470 900,500 870,500 0,30" fill="#000"/>
     <polygon points="900,0 840,0 0,470 0,500 30,500 900,30" fill="#000"/>`),

  /* Haití — blue over red */
  'flag-ht': svg(900, 600,
    `<rect width="900" height="300" fill="#00209f"/>
     <rect y="300" width="900" height="300" fill="#d21034"/>`),

  /* Panamá — white/red/blue/white quarters with blue and red stars */
  'flag-pa': svg(600, 400,
    `<rect width="600" height="400" fill="#fff"/>
     <rect x="300" width="300" height="200" fill="#d21034"/>
     <rect y="200" width="300" height="200" fill="#005293"/>
     <polygon points="150.0,40.0 163.5,81.5 207.1,81.5 171.8,107.1 185.3,148.5 150.0,122.9 114.7,148.5 128.2,107.1 92.9,81.5 136.5,81.5" fill="#005293"/>
     <polygon points="450.0,240.0 463.5,281.5 507.1,281.5 471.8,307.1 485.3,348.5 450.0,322.9 414.7,348.5 428.2,307.1 392.9,281.5 436.5,281.5" fill="#d21034"/>`),

  /* España — red / yellow (double width) / red */
  'flag-es': svg(600, 400,
    `<rect width="600" height="100" fill="#aa151b"/>
     <rect y="100" width="600" height="200" fill="#f1bf00"/>
     <rect y="300" width="600" height="100" fill="#aa151b"/>`),

  /* Italia — green / white / red vertical */
  'flag-it': svg(900, 600,
    `<rect width="300" height="600" fill="#009246"/>
     <rect x="300" width="300" height="600" fill="#fff"/>
     <rect x="600" width="300" height="600" fill="#ce2b37"/>`),

  /* Argentina — light blue / white / light blue with sun */
  'flag-ar': svg(900, 600,
    `<rect width="900" height="200" fill="#74acdf"/>
     <rect y="200" width="900" height="200" fill="#fff"/>
     <rect y="400" width="900" height="200" fill="#74acdf"/>
     <circle cx="450" cy="300" r="46" fill="#f6b40e"/>`),
};
